/**
 * TransactionDetailSheet Component
 *
 * Side panel that shows full transaction details when a row is clicked.
 */

import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "./StatusBadge";
import { PaymentTypeIcon } from "./PaymentTypeIcon";
import type { HistoricalTransaction } from "@/services/paymentService";
import {
  formatDate,
  getCallsign,
  getName,
  formatNominal,
  getPaymentType,
  formatPaymentType,
  getSettlementTime,
} from "../utils/transactionUtils";

interface TransactionDetailSheetProps {
  transaction: HistoricalTransaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%] break-words">{value}</span>
    </div>
  );
}

export function TransactionDetailSheet({ transaction, open, onOpenChange }: TransactionDetailSheetProps) {
  if (!transaction) return null;

  const orderId = transaction.payementData?.transaction_details?.order_id ?? "-";
  const email = transaction.user?.email ?? transaction.payementData?.customer_details?.email ?? "-";
  const phone = transaction.payementData?.customer_details?.phone ?? "-";
  const midtransOrderId = transaction.midtransStatus?.order_id ?? transaction.paymentGatewayData?.order_id ?? "-";
  const transactionId = transaction.midtransStatus?.transaction_id ?? transaction.paymentGatewayData?.transaction_id ?? "-";
  const fraudStatus = transaction.midtransStatus?.fraud_status ?? transaction.paymentGatewayData?.fraud_status ?? "-";
  const statusCode = transaction.midtransStatus?.status_code ?? transaction.paymentGatewayData?.status_code ?? "-";
  const note = transaction.payementData?.transaction_details?.note ?? transaction.paymentGatewayData?.note ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] sm:w-[600px] md:w-[700px] sm:max-w-none overflow-y-auto">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle>Detail Transaksi</SheetTitle>
          <SheetDescription className="font-mono text-xs">
            {transaction._id}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4 px-6 pb-6">
          {/* User Info */}
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Anggota</h4>
            <DetailRow label="Callsign" value={getCallsign(transaction)} />
            <DetailRow label="Nama" value={getName(transaction)} />
            <DetailRow label="Email" value={email} />
            <DetailRow label="Telepon" value={phone} />
          </div>

          <Separator />

          {/* Location */}
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Lokasi</h4>
            <DetailRow label="ORDA" value={transaction.province || "-"} />
            <DetailRow label="ORLOK" value={transaction.kabkot || "-"} />
          </div>

          <Separator />

          {/* Payment Info */}
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Pembayaran</h4>
            <DetailRow label="Order ID" value={<span className="font-mono text-xs break-all">{orderId}</span>} />
            <DetailRow label="Nominal" value={formatNominal(transaction)} />
            <DetailRow
              label="Jenis Pembayaran"
              value={
                <div className="flex items-center gap-2">
                  <PaymentTypeIcon transaction={transaction} />
                  <span>{formatPaymentType(getPaymentType(transaction))}</span>
                </div>
              }
            />
            <DetailRow
              label="Status"
              value={<StatusBadge status={transaction.status} />}
            />
            <DetailRow label="Tanggal" value={formatDate(transaction.createdAt)} />
            <DetailRow label="Settlement" value={getSettlementTime(transaction) || "-"} />
          </div>

          <Separator />

          {/* Midtrans Info */}
          <div className="space-y-1">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Midtrans</h4>
            <DetailRow label="Order ID Midtrans" value={<span className="font-mono text-xs break-all">{midtransOrderId}</span>} />
            <DetailRow label="Transaction ID" value={<span className="font-mono text-xs break-all">{transactionId}</span>} />
            <DetailRow label="Status Code" value={statusCode} />
            <DetailRow label="Fraud Status" value={fraudStatus} />
          </div>

          {/* Note */}
          {note && (
            <>
              <Separator />
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Catatan</h4>
                <p className="text-sm">{note}</p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
